import { PaginationResponse } from '@/app/models/pagination';
import { System } from '@/app/models/system';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { TableCell } from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionMenu, { ItemAction } from '../common/ActionMenu';
import TruncatedTextWithTooltip from '../common/truncatedTextWithTooltip';

interface SystemsTableProps {
  data: PaginationResponse<System>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  page: number;
  rowsPerPage: number;
  setSelectedItem: (item: System | null) => void;
  setDrawerOpen: (open: boolean) => void;
  handleActivate: () => void;
  handleDeactivate: () => void;
}

export default function SystemsTable({
  data,
  setPage,
  setRowsPerPage,
  page,
  rowsPerPage,
  setSelectedItem,
  setDrawerOpen,
  handleActivate,
  handleDeactivate,
}: SystemsTableProps) {
  const { t } = useTranslation(['systems']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const columns: Column[] = [
    { key: 'name', label: t('name') },
    { key: 'description', label: t('description') },
    { key: 'status', label: t('status') },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderRow = (item: System) => {
    return (
      <>
        <TableCell sx={{ ...bodyCellSx }}>{item.name}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip value={item.description} sx={bodyCellSx} size={200} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{item.active ? t('active') : t('inactive')}</TableCell>
        <TableCell sx={{ ...bodyCellSx }} align="right">
          <ActionMenu>
            <ItemAction
              text={t('edit') || ''}
              actionType="edit"
              onClick={() => {
                setSelectedItem(item);
                setDrawerOpen(true);
                handleMenuClose();
              }}
            />
            {item.active ? (
              <ItemAction
                text={t('deactivate') || ''}
                actionType="custom"
                customIcon={CloseIcon}
                onClick={() => {
                  setSelectedItem(item);
                  handleDeactivate();
                  handleMenuClose();
                }}
              />
            ) : (
              <ItemAction
                text={t('activate') || ''}
                actionType="custom"
                customIcon={CheckIcon}
                onClick={() => {
                  setSelectedItem(item);
                  handleActivate();
                  handleMenuClose();
                }}
              />
            )}
          </ActionMenu>
        </TableCell>
      </>
    );
  };

  // Use the system id as the unique key
  const getRowKey = (system: System) => system.id as number;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: data?.totalElements || 0,
    rowsPerPageOptions: [5, 10, 25],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('common:of')} ${count}`,
    labelRowsPerPage: t('common:items.per.page'),
  };

  return (
    <DataTable
      columns={columns}
      data={data?.content || []}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      pagination={paginationConfig}
      size="small"
    />
  );
}
